import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, MapPin, Mountain, Tent, Compass, Loader2, Trees } from "lucide-react";
import { api } from "../lib/api";

/**
 * Reusable search sheet for Discover + Map.
 *
 * - Debounced input (250 ms).
 * - Three result groups:
 *     • "Walks & sites" from /api/locations?q=
 *     • "Basecamps"     from /api/basecamps?q=  (curated day-spots)
 *     • "Places"        from /api/map/os-names?q=  (OS gazetteer — towns, hills, villages…)
 *
 * Props:
 *   open: boolean
 *   onClose(): void
 *   onPickLocation(loc): user clicked a walk/campsite (full location object)
 *   onPickBasecamp(bc): user clicked a curated basecamp (full basecamp object)
 *   onPickPlace(place):  user clicked a UK gazetteer result ({name, lat, lng, type, county})
 *   placeholder: optional input placeholder
 */
export default function SearchSheet({ open, onClose, onPickLocation, onPickBasecamp, onPickPlace, placeholder }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setDebounced("");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const locs = useQuery({
    queryKey: ["search-locations", debounced],
    enabled: open && debounced.length >= 2,
    queryFn: async () => (await api.get("/locations", { params: { q: debounced, limit: 12 } })).data.items,
  });

  const basecamps = useQuery({
    queryKey: ["search-basecamps", debounced],
    enabled: open && debounced.length >= 2,
    queryFn: async () => (await api.get("/basecamps", { params: { q: debounced, limit: 8 } })).data.items,
  });

  const places = useQuery({
    queryKey: ["search-places", debounced],
    enabled: open && debounced.length >= 2,
    queryFn: async () => (await api.get("/map/os-names", { params: { q: debounced } })).data.results,
  });

  if (!open) return null;

  const isLoading = (locs.isFetching || basecamps.isFetching || places.isFetching) && debounced.length >= 2;
  const noResults = debounced.length >= 2 && !isLoading
    && (locs.data?.length || 0) === 0
    && (basecamps.data?.length || 0) === 0
    && (places.data?.length || 0) === 0;

  return (
    <div
      className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-start justify-center p-0 sm:p-6 sm:pt-20"
      onClick={onClose}
      data-testid="search-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface w-full sm:max-w-xl sm:rounded-3xl rounded-none flex flex-col max-h-[100dvh] sm:max-h-[80vh] overflow-hidden"
      >
        {/* Input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-brand-border bg-brand-surface">
          <Search size={18} className="text-brand-subtle shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder || "Search walks, places or postcodes…"}
            className="flex-1 bg-transparent outline-none text-base text-brand-text placeholder:text-brand-subtle"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {q && (
            <button
              data-testid="search-clear"
              onClick={() => { setQ(""); inputRef.current?.focus(); }}
              className="text-brand-subtle p-1"
              aria-label="Clear"
            >
              <X size={16} />
            </button>
          )}
          <button
            data-testid="search-close"
            onClick={onClose}
            className="td-btn-secondary !py-1.5 !px-3 !text-sm"
          >
            Cancel
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto" data-testid="search-results">
          {debounced.length < 2 && (
            <div className="p-5 text-sm text-brand-subtle">
              <p>Try a trail name, a town, a hill or a national park.</p>
              <ul className="mt-3 space-y-1">
                <li>· "Box Hill"</li>
                <li>· "Pennine Way"</li>
                <li>· "Snowdon"</li>
                <li>· "Loch Morlich"</li>
              </ul>
            </div>
          )}
          {isLoading && (
            <div className="p-5 flex items-center gap-2 text-brand-subtle text-sm">
              <Loader2 size={14} className="animate-spin" /> Searching…
            </div>
          )}
          {noResults && (
            <div className="p-5 text-sm text-brand-subtle">No matches. Try a shorter name?</div>
          )}

          {/* Walks & sites */}
          {(locs.data?.length || 0) > 0 && (
            <div>
              <div className="td-section-label px-5 pt-4 pb-2">Walks &amp; sites</div>
              {locs.data.map((l) => {
                const Icon = l.type === "camping" ? Tent : Mountain;
                return (
                  <button
                    key={l.location_id}
                    data-testid={`search-result-loc-${l.location_id}`}
                    onClick={() => { onPickLocation?.(l); onClose(); }}
                    className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-brand-sand transition-colors border-b border-brand-border/40"
                  >
                    <span className="w-9 h-9 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                      <Icon size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-heading text-base text-brand-text truncate">{l.name}</span>
                      <span className="block text-xs text-brand-subtle truncate">
                        {l.type === "camping" ? "Campsite" : "Hiking"} · {l.region || "United Kingdom"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Basecamps (curated day-spots) */}
          {(basecamps.data?.length || 0) > 0 && (
            <div>
              <div className="td-section-label px-5 pt-4 pb-2">Basecamps</div>
              {basecamps.data.map((b) => (
                <button
                  key={b.basecamp_id}
                  data-testid={`search-result-basecamp-${b.basecamp_id}`}
                  onClick={() => { onPickBasecamp?.(b); onClose(); }}
                  className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-brand-sand transition-colors border-b border-brand-border/40"
                >
                  <span className="w-9 h-9 rounded-full bg-brand-accent/15 flex items-center justify-center text-brand-accent shrink-0">
                    <Trees size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-base text-brand-text truncate">{b.name}</span>
                    <span className="block text-xs text-brand-subtle truncate">
                      Basecamp · {b.energy_match === "high_energy" ? "High energy" : "Chill"}
                      {b.cafe_on_site ? " · Cafe on site" : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Places (OS gazetteer) */}
          {(places.data?.length || 0) > 0 && (
            <div>
              <div className="td-section-label px-5 pt-4 pb-2">Places</div>
              {places.data.map((p, i) => (
                <button
                  key={`${p.name}-${i}`}
                  data-testid={`search-result-place-${i}`}
                  onClick={() => { onPickPlace?.(p); onClose(); }}
                  className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-brand-sand transition-colors border-b border-brand-border/40"
                >
                  <span className="w-9 h-9 rounded-full bg-brand-accent/15 flex items-center justify-center text-brand-accent shrink-0">
                    <Compass size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-base text-brand-text truncate">{p.name}</span>
                    <span className="block text-xs text-brand-subtle truncate">
                      {p.type ? p.type + " · " : ""}{p.county || "United Kingdom"}
                    </span>
                  </span>
                  <MapPin size={14} className="text-brand-subtle shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
